import type { EntityWithAuditFields } from '@pkg/db/schema'
import { appError } from '@pkg/shared/errors'
import diff from 'microdiff'
import type { z } from 'zod'
import { type HLCString, parseHLC } from './hlc'
import type { SyncMessage } from './types'

export function validateMerged<T>(table: string, schema: z.ZodType<T>, merged: unknown): T {
  const result = schema.safeParse(merged)
  if (!result.success) {
    throw appError('VALIDATION_FAILED', `Schema validation failed for ${table}: ${result.error.message}`)
  }
  return result.data
}

export function computeDiff<T extends object>(
  existing: T | null | undefined,
  next: T,
  ignoredFields: ReadonlyArray<string>,
): Set<Extract<keyof T, string>> {
  const changes = diff(existing ?? {}, next)
  const filtered = changes
    .filter(c => !ignoredFields.includes(c.path[0] as string))
    .filter(c => !(c.type === 'CREATE' && c.value === undefined))
    .filter(c => !(c.type === 'CHANGE' && c.value === undefined))
    .filter(c => !(c.path.length === 1 && c.type === 'REMOVE'))

  const cols = new Set<Extract<keyof T, string>>()
  for (const c of filtered) cols.add(c.path[0] as Extract<keyof T, string>)
  return cols
}

export interface BuildMessageParams {
  row: string
  column: string
  value: unknown
  intentTs: HLCString
  transactionTs: HLCString
  userId: string
  transformMessage: (msg: SyncMessage) => SyncMessage
}

export function buildMessage(params: BuildMessageParams): SyncMessage {
  const { row, column, value, intentTs, transactionTs, userId, transformMessage } = params
  return transformMessage({ intentTs, transactionTs, userId, row, column, value })
}

export function applyAuditFields<T extends Partial<EntityWithAuditFields>>(
  target: T,
  lastIntentTs: HLCString,
  existing: { createdAt?: string; createdBy?: string } | null,
  uid: string,
): void {
  const { time } = parseHLC(lastIntentTs)
  const nowTime = new Date(time).toISOString()
  ;(target as Record<string, unknown>).createdAt = existing?.createdAt ?? nowTime
  ;(target as Record<string, unknown>).createdBy = existing?.createdBy ?? uid
  ;(target as Record<string, unknown>).updatedAt = nowTime
  ;(target as Record<string, unknown>).updatedBy = uid
}
